// `TrialExpiredOverlay` — soft-block UI for create actions (US-200).
//
// Used as a wrapper around CTAs that require a non-expired plan. When the
// subscription is in soft-block tier, renders a covering layer with the
// "Réactive ton abonnement" call-out. Hard-block redirects happen at the
// router layer (see `HardBlockRedirect`).

import { type ReactNode } from 'react';

import { useT } from '@barel/i18n';
import { Banner, Button } from '@barel/ui';
import { Lock } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

import { useSubscription } from './useSubscription';
import { deriveTrialState } from './TrialStateMachine';

export interface TrialExpiredOverlayProps {
  /** Children rendered when access tier is `full`. */
  children: ReactNode;
  /** Render an inline soft-block banner instead of a covering overlay. */
  inline?: boolean;
}

export function TrialExpiredOverlay({ children, inline }: TrialExpiredOverlayProps) {
  const t = useT('billing');
  const query = useSubscription();
  const state = deriveTrialState(query.data);
  const navigate = useNavigate();
  const goToBilling = () =>
    void navigate({ to: '/settings/$section', params: { section: 'billing' } });

  if (state.accessTier === 'full' && !state.expired) {
    return <>{children}</>;
  }

  if (inline) {
    return (
      <Banner
        tone="error"
        title={t('expired.softTitle')}
        action={
          <Button variant="primary" size="sm" onClick={goToBilling}>
            {t('expired.softCta')}
          </Button>
        }
        data-testid="trial-expired-inline"
      >
        {t('expired.softBody')}
      </Banner>
    );
  }

  return (
    <div data-component="TrialExpiredOverlay" className="relative">
      <div aria-hidden className="pointer-events-none opacity-50">
        {children}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center rounded-lg border border-default bg-bg-overlay/90 p-6 text-center backdrop-blur-sm">
        <Lock size={24} className="text-tertiary" />
        <div className="mt-3 text-base font-semibold text-primary">{t('expired.softTitle')}</div>
        <div className="mt-1 max-w-md text-xs text-secondary">{t('expired.softBody')}</div>
        <Button
          variant="primary"
          size="sm"
          onClick={goToBilling}
          className="mt-4"
          data-testid="trial-expired-overlay-cta"
        >
          {t('expired.softCta')}
        </Button>
      </div>
    </div>
  );
}
